/**
 * Configuration options for setting cookies
 */
export interface CookieOptions {
  maxAge?: number
  expires?: Date
  httpOnly?: boolean
  secure?: boolean
  partitioned?: boolean
  domain?: string
  path?: string
  sameSite?: boolean | 'lax' | 'none' | 'strict'
}

/**
 * Reads and parses a browser cookie as a string.
 * Use getCookieJson for automatic JSON deserialization.
 *
 * @param {string} name The name of the cookie.
 * @returns {string | null} The cookie value as string, or null if not found.
 */
export function getCookie(name: string): string | null {
  // Ensure we are in a browser environment
  if (typeof document === 'undefined') {
    return null
  }

  const cookies = document.cookie.split('; ')
  const cookie = cookies.find(c => c.startsWith(`${encodeURIComponent(name)}=`))

  if (cookie) {
    return decodeURIComponent(cookie.split('=')[1])
  }

  return null
}

/**
 * Reads and parses a browser cookie, automatically deserializing JSON.
 * Returns the parsed object with proper typing.
 *
 * @param {string} name The name of the cookie.
 * @returns {T | null} The parsed cookie value, or null if not found or invalid JSON.
 */
export function getCookieJson<T = unknown>(name: string): T | null {
  const value = getCookie(name)

  if (value === null) {
    return null
  }

  try {
    return JSON.parse(value) as T
  }
  catch {
    // If parsing fails, return null instead of the raw string
    // This maintains type safety
    return null
  }
}

/**
 * Alternative: Get cookie with fallback to string if JSON parsing fails
 */
export function getCookieWithFallback<T = unknown>(
  name: string,
): T | string | null {
  const value = getCookie(name)

  if (value === null) {
    return null
  }

  try {
    return JSON.parse(value) as T
  }
  catch {
    return value // Return raw string if JSON parsing fails
  }
}

/**
 * Sets a browser cookie with optional configuration.
 * Automatically serializes non-string values to JSON.
 *
 * @param {string} name The name of the cookie.
 * @param {unknown} value The value to store in the cookie.
 * @param {CookieOptions} options Optional cookie configuration.
 * @throws {Error} If JSON serialization fails for non-string values.
 */
export function setCookie(
  name: string,
  value: unknown,
  options: CookieOptions = {},
): void {
  // Ensure we are in a browser environment
  if (typeof document === 'undefined') {
    return
  }

  const {
    maxAge,
    expires,
    httpOnly = false,
    secure = false,
    partitioned = false,
    domain,
    path = '/',
    sameSite,
  } = options

  // Serialize the value to JSON if it's not a string
  let serializedValue: string
  try {
    serializedValue = typeof value === 'string' ? value : JSON.stringify(value)
  }
  catch (error) {
    throw new Error(
      `Failed to serialize cookie value: ${error instanceof Error ? error.message : 'Unknown error'}`,
    )
  }

  const encodedValue = encodeURIComponent(serializedValue)
  const encodedName = encodeURIComponent(name)

  let cookieString = `${encodedName}=${encodedValue}`

  if (maxAge !== undefined) {
    cookieString += `; Max-Age=${Math.floor(maxAge)}`
  }

  if (expires) {
    cookieString += `; Expires=${expires.toUTCString()}`
  }

  if (domain) {
    cookieString += `; Domain=${domain}`
  }

  if (path) {
    cookieString += `; Path=${path}`
  }

  if (secure) {
    cookieString += '; Secure'
  }

  if (httpOnly) {
    cookieString += '; HttpOnly'
  }

  if (partitioned) {
    cookieString += '; Partitioned'
  }

  if (sameSite !== undefined) {
    if (sameSite === true || sameSite === 'strict') {
      cookieString += '; SameSite=Strict'
    }
    else if (sameSite === 'lax') {
      cookieString += '; SameSite=Lax'
    }
    else if (sameSite === 'none') {
      cookieString += '; SameSite=None'
    }
  }

  document.cookie = cookieString
}

/**
 * Deletes a browser cookie by setting its expiration to the past.
 *
 * @param {string} name The name of the cookie to delete.
 * @param {CookieOptions} options Optional cookie configuration (domain, path, etc.).
 */
export function deleteCookie(
  name: string,
  options: Pick<CookieOptions, 'path' | 'domain'> = {},
): void {
  if (typeof document === 'undefined') {
    return
  }

  const { path = '/', domain } = options
  const encodedName = encodeURIComponent(name)

  let cookieString = `${encodedName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`

  if (domain) {
    cookieString += `; domain=${domain}`
  }

  document.cookie = cookieString
}

// Type-safe convenience functions for common use cases
export function setCookieString(
  name: string,
  value: string,
  options?: CookieOptions,
): void {
  setCookie(name, value, options)
}

export function setCookieObject<T>(
  name: string,
  value: T,
  options?: CookieOptions,
): void {
  setCookie(name, value, options)
}
